// frontend/src/hooks/useNotifications.js
/**
 * Notification Hook
 * 
 * React Query-based hook for fetching and managing notifications.
 * Features:
 * - 60-second polling with jitter (prevents thundering herd)
 * - Visibility-aware (pauses when tab is hidden)
 * - Toast notifications for new arrivals
 * - Optimistic updates for mark as read
 * 
 * Jitter Pattern:
 * - Base interval: 60 seconds
 * - Random jitter: 0-5 seconds added
 * - This spreads 500 users' requests over 5 seconds instead of hitting simultaneously
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/notificationService';
import toast from '../utils/toast';
import { getNotificationToastPreview, isDigestNotification } from '../utils/notificationToastUtils';
import { safeNavigateToNotificationUrl } from '../utils/urlUtils';

// Query keys
const NOTIFICATION_KEYS = {
  all: ['notifications'],
  list: (params) => ['notifications', 'list', params],
  unreadCount: ['notifications', 'unread_count'],
  byCategory: ['notifications', 'by_category'],
};

// Polling configuration
const BASE_POLLING_INTERVAL = 60 * 1000;  // 60 seconds base
const JITTER_MAX = 5 * 1000;               // 0-5 seconds random jitter

// When more than this many new notifications arrive in one poll cycle, collapse
// them into a single summary toast rather than flooding the screen.
const TOAST_BATCH_THRESHOLD = 3;

/**
 * Get polling interval with jitter.
 * This prevents "thundering herd" - all 500 users hitting the server at the exact same time.
 * 
 * @returns {number} Polling interval in milliseconds (60-65 seconds)
 */
const getPollingIntervalWithJitter = () => {
  const jitter = Math.random() * JITTER_MAX;
  return BASE_POLLING_INTERVAL + jitter;
};

/**
 * Main notifications hook
 * @param {Object} options - Hook options
 * @param {Object} options.params - Query parameters for filtering notifications
 * @param {boolean} options.enablePolling - Whether to enable polling (default: true)
 * @param {boolean} options.enableToasts - Whether to show toast notifications for new items (default: true)
 * @returns {Object} Notification state and actions
 */
export const useNotifications = (options = {}) => {
  const { params = { limit: 20 }, enablePolling = true, enableToasts = true } = options;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Anchor timestamp: notifications older than this were "already known" at mount time.
  // Using created_at comparison instead of an ID set means we are resilient to:
  //   - pagination gaps (limit:20 can miss older items)
  //   - hook remounts resetting in-memory state
  //   - refetchOnWindowFocus bursts after the tab has been idle
  const loadedAt = useRef(null);

  // Fetch notifications with jitter polling
  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: NOTIFICATION_KEYS.list(params),
    queryFn: () => notificationService.getNotifications(params),
    staleTime: 30 * 1000, // 30 seconds
    // Dynamic interval with jitter - prevents thundering herd
    refetchInterval: enablePolling ? getPollingIntervalWithJitter : false,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden (visibility-aware)
    refetchOnWindowFocus: false, // Interval polling handles freshness; focus-refetch causes toast bursts
  });

  // Fetch unread count (lightweight polling with jitter)
  const { data: unreadData } = useQuery({
    queryKey: NOTIFICATION_KEYS.unreadCount,
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 30 * 1000,
    // Dynamic interval with jitter - prevents thundering herd
    refetchInterval: enablePolling ? getPollingIntervalWithJitter : false,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden (visibility-aware)
    refetchOnWindowFocus: false,
  });

  // Extract data
  const notifications = notificationsData?.results || [];
  const unreadCount = unreadData?.unread_count ?? notificationsData?.unread_count ?? 0;
  // Extract pagination metadata (DRF pagination includes 'next' field)
  const paginationNext = notificationsData?.next || null;

  // Show toast for a single new notification.
  // Defined before the detection effect so it is in scope when the effect runs.
  const showNotificationToast = useCallback((notification) => {
    const iconType = notification.icon_type || 'info';
    const { title, subtitle } = getNotificationToastPreview(notification);

    // Digests are actionable rollups — warning colour matches "needs review".
    const toastFn = isDigestNotification(notification)
      ? toast.warning
      : ({
          success: toast.success,
          error: toast.error,
          warning: toast.warning,
          info: toast.info,
        }[iconType] || toast.info);

    toastFn(
      (t) => (
        <div
          className="flex items-start cursor-pointer min-w-0"
          onClick={() => {
            toast.dismiss(t.id);
            if (notification.action_url) {
              safeNavigateToNotificationUrl(notification.action_url, navigate);
            }
          }}
        >
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-white line-clamp-2">{title}</p>
            {subtitle ? (
              <p className="text-xs text-white/90 mt-1 line-clamp-1">{subtitle}</p>
            ) : null}
          </div>
        </div>
      ),
      {
        duration: 5000,
        position: 'top-right',
      }
    );
  }, [navigate]);

  // Detect new notifications and show toasts.
  //
  // Strategy: compare each notification's created_at against the loadedAt anchor
  // (set to the newest notification's timestamp on first data arrival).
  // This is resilient to:
  //   - Hook remounts (anchor resets cleanly on first data arrival)
  //   - Pagination gaps (no ID set = no "phantom new" from unseen pages)
  //   - Tab idle + refocus (loadedAt anchors at mount; older items are silently ignored)
  //
  // Batching: more than TOAST_BATCH_THRESHOLD new notifications in one cycle collapses
  // into a single summary toast, preventing a wall of alerts for HR admins who receive
  // high fan-out notifications (document submissions, compliance alerts, etc.).
  useEffect(() => {
    if (!notifications.length) return;

    // First data arrival: anchor to the newest known notification.
    // Nothing is toasted — these all existed before the user opened the tab.
    if (!loadedAt.current) {
      loadedAt.current = notifications[0].created_at;
      return;
    }

    // Toasts disabled on the notifications page itself to prevent redundancy.
    if (!enableToasts) return;

    // Collect notifications genuinely newer than our anchor that are still unread.
    // The is_read guard prevents re-toasting items the user dismissed in another tab.
    const incoming = notifications.filter(
      (n) => !n.is_read && n.created_at > loadedAt.current
    );

    if (!incoming.length) return;

    // Advance the anchor so the next poll cycle doesn't re-toast these same items.
    loadedAt.current = incoming[0].created_at;

    if (incoming.length > TOAST_BATCH_THRESHOLD) {
      toast.info(`You have ${incoming.length} new notifications`, {
        duration: 6000,
        position: 'top-right',
      });
    } else {
      incoming.forEach(showNotificationToast);
    }
  }, [notifications, enableToasts, showNotificationToast]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId) => notificationService.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEYS.all });

      // Snapshot previous value
      const previousNotifications = queryClient.getQueryData(NOTIFICATION_KEYS.list(params));

      // Optimistically update
      queryClient.setQueryData(NOTIFICATION_KEYS.list(params), (old) => {
        if (!old) return old;
        return {
          ...old,
          results: old.results.map((n) =>
            n.id === notificationId ? { ...n, is_read: true } : n
          ),
          unread_count: Math.max(0, (old.unread_count || 0) - 1),
        };
      });

      return { previousNotifications };
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      queryClient.setQueryData(NOTIFICATION_KEYS.list(params), context.previousNotifications);
      toast.error('Failed to mark notification as read');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.unreadCount });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: (category) => notificationService.markAllAsRead(category),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEYS.all });

      const previousNotifications = queryClient.getQueryData(NOTIFICATION_KEYS.list(params));

      // Optimistically mark all as read
      queryClient.setQueryData(NOTIFICATION_KEYS.list(params), (old) => {
        if (!old) return old;
        return {
          ...old,
          results: old.results.map((n) => ({ ...n, is_read: true })),
          unread_count: 0,
        };
      });

      return { previousNotifications };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(NOTIFICATION_KEYS.list(params), context.previousNotifications);
      toast.error('Failed to mark all notifications as read');
    },
    onSuccess: (data) => {
      toast.success(`Marked ${data.marked_count} notifications as read`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    },
  });

  // Mark as resolved mutation
  const markAsResolvedMutation = useMutation({
    mutationFn: (notificationId) => notificationService.markAsResolved(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    },
    onError: () => {
      toast.error('Failed to mark notification as resolved');
    },
  });

  return {
    // Data
    notifications,
    unreadCount,
    loading: isLoading,
    error: error?.message || null,
    paginationNext, // DRF pagination 'next' field (null when no more pages)

    // Actions
    fetchNotifications: refetch,
    markAsRead: (id) => markAsReadMutation.mutate(id),
    markAllAsRead: (category = null) => markAllAsReadMutation.mutate(category),
    markAsResolved: (id) => markAsResolvedMutation.mutate(id),

    // Mutation states
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
};

/**
 * Hook for just the unread count (lightweight)
 * Use this when you only need the badge count without full notification data
 */
export const useUnreadNotificationCount = () => {
  const { data, isLoading } = useQuery({
    queryKey: NOTIFICATION_KEYS.unreadCount,
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 30 * 1000,
    // Dynamic interval with jitter - prevents thundering herd
    refetchInterval: getPollingIntervalWithJitter,
    refetchIntervalInBackground: false, // Visibility-aware polling
  });

  return {
    unreadCount: data?.unread_count ?? 0,
    loading: isLoading,
  };
};

/**
 * Hook for notifications grouped by category
 */
export const useNotificationCategories = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: NOTIFICATION_KEYS.byCategory,
    queryFn: () => notificationService.getByCategory(),
    staleTime: 60 * 1000,
  });

  return {
    categories: data?.categories || [],
    totalUnread: data?.total_unread ?? 0,
    loading: isLoading,
    error: error?.message || null,
  };
};

export default useNotifications;
